/* ============================================================================
 * Button.jsx
 * ============================================================================
 * Componente de botón reutilizable con múltiples variantes y estados.
 *
 * Botón versátil que soporta diferentes estilos visuales (primary, secondary,
 * pro, share), estados (disabled, loading), y modos de presentación (text, icon).
 * Incluye indicador de carga visual y prevención de clics durante carga/disabled.
 *
 * Características:
 * - 4 variantes visuales: primary, secondary, pro, share
 * - 2 modos de presentación: solo texto (text) o solo icono (icon)
 * - Estado de carga con spinner animado (@uiball/loaders)
 * - Prevención de clics múltiples durante loading/disabled
 * - Soporte para formularios externos mediante prop "form"
 * - Accesibilidad mejorada con atributos ARIA
 * - Clases CSS adicionales mediante array "clazzes"
 *
 * @module Button
 * ========================================================================== */

import { useCallback, useMemo } from "react";

import classNames from "classnames";

import { Ring } from "@uiball/loaders";

import { isEmpty } from "../../utils/validators";

/* ============================================================================
 * Button Component
 * ========================================================================== */

/**
 * Componente de botón con variantes de estilo y estados de carga.
 *
 * @param {Object} props - Props del componente
 * @param {string} [props.type="button"] - Tipo de botón HTML (button, submit, reset)
 * @param {string} [props.form] - ID del formulario asociado (para submit fuera del form)
 * @param {string[]} [props.clazzes=[]] - Array de clases CSS adicionales
 * @param {React.ReactNode} props.children - Contenido del botón (texto, iconos, etc)
 * @param {boolean} [props.primary=false] - Variante primary (fondo gradient negro)
 * @param {boolean} [props.secondary=false] - Variante secondary (fondo blanco, borde gris)
 * @param {boolean} [props.pro=false] - Variante pro (fondo gradient pro)
 * @param {boolean} [props.share=false] - Variante share (fondo verde éxito)
 * @param {boolean} [props.text=false] - Modo solo texto (oculta iconos)
 * @param {boolean} [props.icon=false] - Modo solo icono (oculta labels)
 * @param {string} [props.ariaLabel] - Etiqueta accesible (recomendada en modo icon)
 * @param {string} [props.ariaDescribedby] - ID(s) de elementos descriptivos
 *                                            asociados (helpers, mensajes
 *                                            de estado). Cumple SC 1.3.1.
 * @param {boolean} [props.disabled=false] - Estado deshabilitado del botón
 * @param {boolean} [props.loading=false] - Estado de carga (muestra spinner)
 * @param {Function} [props.onClick] - Handler de clic simple
 * @param {Function} [props.onDoubleClick] - Handler de doble clic
 * @returns {JSX.Element} Botón con wrapper contenedor
 *
 * @example
 * // Botón primary básico
 * <Button primary onClick={handleClick}>
 *   Guardar
 * </Button>
 *
 * @example
 * // Botón secondary con icono
 * <Button secondary>
 *   <span className="icon icon-download" />
 *   <span className="label">Descargar</span>
 * </Button>
 *
 * @example
 * // Botón pro solo icono en estado de carga (con ariaLabel para accesibilidad)
 * <Button pro icon loading ariaLabel="Añadir a favoritos">
 *   <span className="icon icon-star" />
 * </Button>
 *
 * @example
 * // Botón share con clases adicionales
 * <Button share clazzes={["custom-class", "another-class"]}>
 *   Compartir archivos
 * </Button>
 *
 * @example
 * // Botón submit asociado a formulario externo
 * <Button type="submit" form="upload-form" primary disabled={!isValid}>
 *   Subir archivos
 * </Button>
 */
function Button(props) {
  const {
    type = "button",
    form,
    clazzes = [],
    children,
    primary = false,
    secondary = false,
    pro = false,
    share = false,
    text = false,
    icon = false,
    ariaLabel,
    ariaDescribedby,
    disabled = false,
    onClick,
    onDoubleClick,
    loading = false,
  } = props;

  /* --------------------------------------------------------------------------
   * Valores derivados
   * ------------------------------------------------------------------------ */

  /**
   * Determina si el botón debe estar deshabilitado.
   * True si está explícitamente disabled o en estado loading.
   *
   * @type {boolean}
   */
  const isDisabled = useMemo(() => {
    return disabled || loading;
  }, [disabled, loading]);

  /**
   * Determina el color del spinner según la variante.
   * Variantes con fondo oscuro usan spinner blanco,
   * secondary usa spinner gris para mejor contraste.
   *
   * @type {string}
   */
  const spinnerColor = useMemo(() => {
    if (secondary) return "#6b7280";
    return "white";
  }, [secondary]);

  /* --------------------------------------------------------------------------
   * Handlers
   * ------------------------------------------------------------------------ */

  /**
   * Handler de clic del botón.
   * El atributo disabled del HTML ya previene eventos,
   * este handler simplemente propaga al callback.
   *
   * @param {React.MouseEvent} evt - Evento de clic
   */
  const handleClick = useCallback((evt) => onClick?.(evt), [onClick]);

  /**
   * Handler de doble clic del botón.
   *
   * @param {React.MouseEvent} evt - Evento de doble clic
   */
  const handleDoubleClick = useCallback(
    (evt) => onDoubleClick?.(evt),
    [onDoubleClick],
  );

  /* --------------------------------------------------------------------------
   * Render
   * ------------------------------------------------------------------------ */

  return (
    <div className="button-container">
      <button
        type={type}
        {...(!isEmpty(form) && { form })}
        className={classNames(
          "button",
          {
            "button-primary": primary,
            "button-secondary": secondary,
            "button-text": text,
            "button-icon": icon,
            "button-pro": pro,
            "button-share": share,
            "button-disabled": disabled,
            "button-loading": loading,
          },
          clazzes,
        )}
        disabled={isDisabled}
        aria-busy={loading}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedby}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
      >
        {/* Spinner de carga - oculto para lectores de pantalla */}
        {loading && (
          <Ring
            size={20}
            lineWeight={7}
            speed={2}
            color={spinnerColor}
            aria-hidden="true"
          />
        )}
        {/* Contenido del botón (iconos, labels, etc) */}
        {children}
      </button>
    </div>
  );
}

Button.displayName = "Button";

export default Button;
